import {useTableSort} from '@flexinet/ui-components';

import {
    flattenPages,
    useBillableUnitsInfiniteQuery,
} from './useBillableUnitsQuery';
import {useBillableUnitsSearch} from './useBillableUnitsSearch';
import {BillableUnit} from '../types';

const matchesKeyword = (item: BillableUnit, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.name,
        item.description,
        item.meterId,
        item.meter?.meterCode,
        item.meter?.name,
        item.status,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase().includes(needle));
};

export const useBillableUnits = () => {
    const query = useBillableUnitsInfiniteQuery();
    const {keyword} = useBillableUnitsSearch();

    // The full loaded set BEFORE keyword filtering: the window the footer's
    // "N of M loaded" is measured against.
    const items = flattenPages(query.data?.pages);

    const needle = keyword.trim().toLowerCase();

    // ⚠️ Sort and keyword filter are CLIENT-SIDE, over the pages LOADED SO FAR. Under
    // token pagination the browser cannot order or search rows it has not fetched;
    // that is a property of the model, not a bug (§9). The API cannot do either for
    // us today: listBillableUnits ignores `sort` and has no name filter.
    //
    // Default: newest-created first, which is the order the API returns
    // (record-type-created-index, scanIndexForward: false), so the first paint agrees
    // with the server. `created: AWSDateTime` is nullable in the schema, but the API
    // stamps it on every create and orders by it; there is no non-null alternative.
    // Sort state is component-level (useTableSort holds it), never the shared store.
    const {sortStatus, setSortStatus, sorted} = useTableSort<BillableUnit>(
        items.filter(item => matchesKeyword(item, needle)),
        {columnAccessor: 'created', direction: 'desc'},
    );

    const pages = query.data?.pages ?? [];
    const lastPage = pages[pages.length - 1];

    return {
        items,
        visibleItems: sorted,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        isFetchingMore: query.isFetchingNextPage,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
